import type { Handle } from 'remix/ui'
import { currentHost } from '../config/current-host'

interface BrandMarkProps {
  size?: number
  className?: string
  /** Visual variant for different surfaces. Kept for API compatibility with
   * the upstream mascot art; all variants render the same neutral mark. */
  variant?: 'mark' | 'camera' | 'timeline' | 'share' | 'icon'
  /**
   * Home LCP art. Renders a same-size spacer; the visible mark lives in
   * index.html `#boot-hero` so first paint is not gated on the SPA bundle.
   */
  priority?: boolean
}

export function BrandMark(handle: Handle<BrandMarkProps>) {
  return () => {
    const { size = 56, className, priority = false } = handle.props

    if (priority) {
      return (
        <div
          className={className}
          style={{ width: size, height: size }}
          aria-hidden="true"
        />
      )
    }

    // The host's mark, as a single inlined path: the app ships zero brand
    // image assets, and index.html's #boot-hero renders the identical path
    // before any JS runs (the worker rewrites both from the same
    // `brand.markPath`). This used to be a second hardcoded copy of OKB's
    // path, which meant a new host got its own boot mark and OKB's everywhere
    // else. Drawn in `currentColor` so one path serves both themes — white on
    // the dark theme, ink on light.
    return (
      <svg
        className={className}
        width={size}
        height={size}
        viewBox="-14 -14 138 138"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          fill="currentColor"
          d={currentHost().brand.markPath}
        />
      </svg>
    )
  }
}
